import React from 'react';
import {
  Alert, AlertIcon, AlertText, Box, Button, ButtonText, HStack, Icon, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import {
  AlertCircle, Clock, Info, XCircle,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import type { StartGateReasonKind } from './startGateReason';

export type StartGateResolvedReason = {
  kind: StartGateReasonKind;
  testID: string;
  primary: string;
};

export type StartGateFact = {
  testID: string;
  text: string;
};

function ReasonIcon({ kind }: { kind: StartGateReasonKind }) {
  if (kind === 'checking') return <Spinner size="small" color="$textSubtle" />;
  if (kind === 'action') return <Icon as={AlertCircle} size="sm" color="$statusWarning" />;
  if (kind === 'info') return <Icon as={Info} size="sm" color="$textSubtle" />;
  return <Icon as={Clock} size="sm" color="$textSubtle" />;
}

/** Scheduled start or join-closed line (title row or standalone StartGate). */
export function StartGateFactLine({ fact }: { fact: StartGateFact }) {
  return (
    <Text size="md" color="$textSubtle" testID={fact.testID}>
      {fact.text}
    </Text>
  );
}

const ROW_NOWRAP = { flexWrap: 'nowrap' as const };

/**
 * Page header row: title block on the left, gate status and button on the right,
 * one hairline under it. Used by the pre-flight page with or without a live gate.
 */
export function StartGateHeaderRow({
  leading,
  trailing,
  testID = 'start-gate-header-row',
}: {
  leading: React.ReactNode;
  trailing?: React.ReactNode;
  testID?: string;
}) {
  return (
    <HStack
      space="lg"
      pb="$6"
      alignItems="center"
      justifyContent="space-between"
      borderBottomWidth={1}
      borderColor="$borderLight"
      style={ROW_NOWRAP}
      testID={testID}
    >
      <Box flexShrink={1}>{leading}</Box>
      {trailing ? (
        <HStack space="lg" alignItems="center" flexShrink={0} style={ROW_NOWRAP}>
          {trailing}
        </HStack>
      ) : null}
    </HStack>
  );
}

/** "{{ready}} of {{total}} ready to start" — status bar or the page's students header. */
export function StartGateReadyCount({
  readyCount,
  total,
  variant = 'bar',
}: {
  readyCount: number;
  total: number;
  variant?: 'bar' | 'columnHeader';
}) {
  const { t } = useTranslation();
  const label = t('oralExam.lecturer.startGate.clearedCount', {
    defaultValue: '{{ready}} of {{total}} ready to start',
    ready: readyCount,
    total,
  });
  if (variant === 'columnHeader') {
    return (
      <Text size="sm" color="$textSubtle" numberOfLines={1} testID="start-gate-ready-count">
        {label}
      </Text>
    );
  }
  return (
    <Text size="md" color="$textHeading" fontFamily="$heading" testID="start-gate-ready-count">
      {label}
    </Text>
  );
}

/**
 * StartGateStatusBar
 * ----
 * Ready count, the single most important reason the supervisor cannot start yet,
 * and the start button — one hairline under the row, no box. With `leading`, the
 * row is the page header: the leading block replaces the ready count, which the
 * page shows in its students column header instead.
 */
export function StartGateStatusBar({
  readyCount,
  total,
  reason,
  submitLabel,
  submitDisabled,
  onSubmit,
  leading,
}: {
  readyCount: number;
  total: number;
  reason: StartGateResolvedReason | null;
  submitLabel: string;
  submitDisabled: boolean;
  onSubmit: () => void;
  leading?: React.ReactNode;
}) {
  const reasonNode = reason ? (
    <HStack space="sm" alignItems="center" testID={reason.testID} flexShrink={1}>
      <ReasonIcon kind={reason.kind} />
      <Text size="sm" color="$textSubtle" numberOfLines={1} flexShrink={1}>{reason.primary}</Text>
    </HStack>
  ) : null;
  const submitButton = (
    <Button
      onPress={onSubmit}
      isDisabled={submitDisabled}
      testID="start-gate-submit"
    >
      <ButtonText>{submitLabel}</ButtonText>
    </Button>
  );

  if (leading) {
    return (
      <StartGateHeaderRow
        leading={leading}
        trailing={(
          <>
            {reasonNode}
            {submitButton}
          </>
        )}
        testID="start-gate-status-bar"
      />
    );
  }

  return (
    <HStack
      space="md"
      mt="$5"
      pb="$4.5"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      borderBottomWidth={1}
      borderColor="$borderLight"
      testID="start-gate-status-bar"
    >
      <StartGateReadyCount readyCount={readyCount} total={total} />
      <HStack space="lg" alignItems="center" flexWrap="wrap" justifyContent="flex-end">
        {reasonNode}
        {submitButton}
      </HStack>
    </HStack>
  );
}

/** Session is over: replaces the status bar so no dead Start button remains. */
export function StartGateEndedBanner({ testID, message }: { testID: string; message: string }) {
  return (
    <Alert action="warning" variant="accent" borderRadius="$md">
      <AlertIcon as={XCircle} mr="$2" />
      <AlertText color="$textOnWarningSubtle" testID={testID}>
        {message}
      </AlertText>
    </Alert>
  );
}

/** AI examiner unavailable on a group slot: Retry, or cancel for a technical problem. */
export function StartGateExaminerAlert({
  message,
  onRetry,
  showCancelTechnical,
  isCancellingTechnical,
  onCancelTechnical,
  cancelTechnicalError,
}: {
  message: string;
  onRetry: () => void;
  showCancelTechnical: boolean;
  isCancellingTechnical: boolean;
  onCancelTechnical: () => void;
  cancelTechnicalError: string | null;
}) {
  const { t } = useTranslation();
  return (
    <Alert
      action="error"
      variant="accent"
      borderRadius="$md"
      alignItems="flex-start"
      testID="start-gate-examiner-unavailable"
    >
      <AlertIcon as={AlertCircle} mr="$2" mt="$0.5" />
      <VStack space="sm" flex={1}>
        <AlertText color="$textOnErrorSubtle">{message}</AlertText>
        <HStack space="sm" flexWrap="wrap">
          <Button
            variant="outline"
            onPress={onRetry}
            testID="start-gate-examiner-retry"
          >
            <ButtonText>{t('common.buttons.retry', { defaultValue: 'Retry' })}</ButtonText>
          </Button>
          {showCancelTechnical ? (
            <Button
              action="negative"
              variant="outline"
              onPress={onCancelTechnical}
              isDisabled={isCancellingTechnical}
              testID="start-gate-cancel-technical"
            >
              <ButtonText>
                {t('oralExam.lecturer.startGate.cancelTechnicalProblem', {
                  defaultValue: 'Cancel session (technical problem)',
                })}
              </ButtonText>
            </Button>
          ) : null}
        </HStack>
        {cancelTechnicalError ? (
          <Text size="sm" color="$textOnErrorSubtle" testID="start-gate-cancel-technical-error">
            {cancelTechnicalError}
          </Text>
        ) : null}
      </VStack>
    </Alert>
  );
}
